import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  acquireSubscription,
  type SubscriptionHandle,
} from "../api/realtime";
import { chatQueryKeys, invalidateChatRoomLists } from "../api/chatApi";
import type { ChatMessage, RealtimeEvent } from "../types";

interface InfiniteData {
  pages: {
    data: ChatMessage[];
    meta: { has_more: boolean; next_cursor: string | null; count: number };
  }[];
  pageParams: unknown[];
}

function patch(old: InfiniteData | undefined, evt: RealtimeEvent): InfiniteData {
  const base: InfiniteData = old ?? {
    pages: [{ data: [], meta: { has_more: false, next_cursor: null, count: 0 } }],
    pageParams: [null],
  };
  if (evt.type === "message.created") {
    // Convention: pages[0] is the newest 50; older history accumulates as
    // pages[1], pages[2], … via fetchNextPage. So newly-arrived live messages
    // append to pages[0], not to the last page.
    const exists = base.pages.some((p) =>
      p.data.some((m) => m.id === evt.message.id),
    );
    if (exists) return base;
    const pages = base.pages.slice();
    pages[0] = {
      ...pages[0],
      data: [...pages[0].data, evt.message],
      meta: { ...pages[0].meta, count: pages[0].meta.count + 1 },
    };
    return { ...base, pages };
  }
  if (evt.type === "message.updated") {
    return {
      ...base,
      pages: base.pages.map((p) => ({
        ...p,
        data: p.data.map((m) =>
          m.id === evt.message.id ? evt.message : m,
        ),
      })),
    };
  }
  if (evt.type === "message.deleted") {
    return {
      ...base,
      pages: base.pages.map((p) => ({
        ...p,
        data: p.data.map((m) =>
          m.id === evt.id
            ? {
                ...m,
                body: "",
                is_deleted: true,
                deleted_at: new Date().toISOString(),
              }
            : m,
        ),
      })),
    };
  }
  return base;
}

// A busy room publishes one event per message; the room list is the most
// expensive list in the app to serve — invalidateChatRoomLists coalesces a burst
// (from here, the chat-user nudge and mark-read alike) into one refetch.

interface ChatRoomLiveOptions {
  onTyping?: (userId: string, userName: string) => void;
  onNewMessage?: (message: ChatMessage) => void;
}

export function useChatRoomLive(
  roomId: string | null,
  opts: ChatRoomLiveOptions = {},
) {
  const qc = useQueryClient();
  const onTypingRef = useRef(opts.onTyping);
  onTypingRef.current = opts.onTyping;
  const onNewMessageRef = useRef(opts.onNewMessage);
  onNewMessageRef.current = opts.onNewMessage;

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    let handle: SubscriptionHandle | null = null;
    let publicationHandler: ((ctx: { data: unknown }) => void) | null = null;
    let errorHandler: ((ctx: unknown) => void) | null = null;
    const refreshLists = () => invalidateChatRoomLists(qc);

    (async () => {
      try {
        const channel = `chat:${roomId}`;
        // Refcounted: useChatGlobalLive holds its own handle on the same
        // channel, so releasing ours on unmount must not tear it down.
        const h = await acquireSubscription(channel);
        if (cancelled) {
          h.release();
          return;
        }
        handle = h;

        publicationHandler = (ctx: { data: unknown }) => {
          const evt = ctx.data as RealtimeEvent;
          if (evt.type === "typing") {
            onTypingRef.current?.(evt.user_id, evt.user_name);
            return;
          }
          // Update the message cache directly — no invalidation needed here
          // because the messages query is NOT stale; we just patched it in place.
          qc.setQueryData<InfiniteData>(
            chatQueryKeys.messages(roomId),
            (old) => patch(old, evt),
          );
          if (evt.type === "message.created") {
            onNewMessageRef.current?.(evt.message);
          }
          refreshLists();
        };
        h.sub.on("publication", publicationHandler);

        errorHandler = (ctx: unknown) => {
          console.warn("[GG] Subscription error for", channel, ctx);
        };
        h.sub.on("error", errorHandler);
      } catch (err) {
        console.warn("[GG] Centrifugo unavailable:", err);
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (publicationHandler) handle?.sub.off("publication", publicationHandler);
        if (errorHandler) handle?.sub.off("error", errorHandler);
        handle?.release();
      } catch {
        // ignore
      }
    };
  }, [roomId, qc]);
}
